import { useEffect } from "react";
import { Drawer, useToast } from "barte-design-system";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { usePostHog } from "@posthog/react";

import { ROUTES_PAYFAC, TRACK_EVENTS } from "@payfac/constants";
import { useGetPendencyDetails, usePatchPendency } from "@payfac/services";

import {
  PendencyHeader,
  PendenciesCarousel,
  PendingAlreadyReviewedList,
  usePendenciesCarousel,
} from "./components";

import type { PendenciesShowProps } from "./show.type";
import styles from "./show.module.css";

export const PendenciesShow = ({ isOpen, onClose, id }: PendenciesShowProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const postHog = usePostHog();
  const toast = useToast();

  const { data, isLoading } = useGetPendencyDetails(id ?? "");
  const { mutateAsync: patchPendency, isPending } = usePatchPendency();

  const carousel = usePendenciesCarousel({
    errors: data?.data?.context?.errors,
    isLoading,
  });

  const onHandleNavigateToPlan = () => {
    postHog.capture(TRACK_EVENTS.PENDENCIES.SHOW_CLICK_NAVIGATE_PLAN);
    navigate(
      ROUTES_PAYFAC.RATE_PLANS.SHOW.fullPath(
        data?.data?.context?.sellerPlanTemplateId,
      ),
    );
  };

  const handleSave = async () => {
    const errors = data?.data?.context?.errors ?? [];
    const now = new Date().toISOString();
    const acceptedKeys = new Set(carousel.accepted.map((e) => e.key));

    postHog.capture(TRACK_EVENTS.PENDENCIES.SUBMIT_INTENT, {
      pendency_id: id,
      consented_count: acceptedKeys.size,
      is_done: carousel.done,
    });

    const updatedErrors = errors.map((error) => {
      const key = `${error.installment}-${error.brand}-${error.captureType}`;
      if (acceptedKeys.has(key)) return { ...error, consentedAt: now };
      return error;
    });

    const res = await patchPendency({ id, errors: updatedErrors });

    if (res.success) {
      postHog.capture(TRACK_EVENTS.PENDENCIES.SUBMIT_SUCCESS, { pendency_id: id });
      toast.success({ title: res.message });
      onClose();
    } else {
      postHog.capture(TRACK_EVENTS.PENDENCIES.SUBMIT_ERROR, {
        pendency_id: id,
        message: res.message,
      });
      toast.error({ title: res.message });
    }
  };

  useEffect(() => {
    postHog.capture(TRACK_EVENTS.PENDENCIES.SHOW_VIEWED);
  }, []);

  const saveLabel = carousel.done
    ? t("payfac.pagePendenciesCarouselSave")
    : t("payfac.pagePendenciesCarouselSaveProgress", {
      count: carousel.accepted.length,
    });

  return (
    <Drawer
      isOpen={isOpen}
      title={t("payfac.pagePendenciesShowTitle")}
      onClose={onClose}
      footer={{
        primaryButton: {
          children: saveLabel,
          onClick: handleSave,
          loading: isPending,
          disabled: carousel.accepted.length === 0,
        },
        secondaryButton: {
          children: t("payfac.pagePendenciesShowButtonCancel"),
          onClick: onClose,
        },
      }}
    >
      <div className={styles.container}>
        <PendencyHeader
          isLoading={isLoading}
          title={data?.data?.context?.sellerPlanTemplate?.name}
          sellerName={data?.data?.context?.seller?.name}
          count={data?.data?.context?.errors?.length ?? 0}
          onClick={onHandleNavigateToPlan}
        />

        <PendenciesCarousel
          isLoading={isLoading}
          transitionState={carousel.transitionState}
          done={carousel.done}
          total={carousel.total}
          current={carousel.current}
          currentIdx={carousel.currentIdx}
          progressPct={carousel.progressPct}
          canUndo={carousel.canUndo}
          totalImpact={carousel.totalImpact}
          remaining={carousel.remaining}
          acceptedCount={carousel.accepted.length}
          skippedCount={carousel.skipped.length}
          resolve={carousel.resolve}
          undo={carousel.undo}
          handleReview={carousel.handleReview}
        />

        {!isLoading && (
          <PendingAlreadyReviewedList
            accepted={carousel.accepted}
            skipped={carousel.skipped}
            onRemoveItem={carousel.removeItem}
          />
        )}
      </div>
    </Drawer>
  );
};
